import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '../api/client'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token') || null)
  const initialized = ref(false)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin' || user.value?.isAdmin === true)
  const userName = computed(() => user.value?.name || user.value?.username || user.value?.email || 'User')
  const userEmail = computed(() => user.value?.email || '')

  // Subscription/Trial getters
  const subscriptionTier = computed(() => user.value?.subscriptionTier || 'free')
  const isPro = computed(() => subscriptionTier.value === 'pro')
  const isTrialActive = computed(() => user.value?.trialActive === true)
  // Per-module Pro grant list from /api/auth/me. When the user has a granted
  // subscription with scoped EnabledModules, this is exactly that list; an
  // empty list combined with tier=pro means a legacy/all-modules Pro source
  // (trial, Stripe sub, pre-per-module admin grant), which we treat as "all
  // modules enabled" so existing Pro users aren't retroactively locked out.
  const enabledModules = computed(() => Array.isArray(user.value?.enabledModules) ? user.value.enabledModules : [])
  /**
   * True when the user has Pro access for the given product module ("brb",
   * "q", "announcements", etc.). Use this for product-page gating instead of
   * the global isPro bool, since admins can grant Pro for a subset of modules.
   */
  function hasProFor(moduleName) {
    if (!isPro.value && !isTrialActive.value) return false
    // Empty list means "all modules" (legacy Pro, Stripe sub, trial).
    if (enabledModules.value.length === 0) return true
    return enabledModules.value.includes(moduleName)
  }
  const trialExpired = computed(() => user.value?.trialExpired === true)
  const trialDaysRemaining = computed(() => user.value?.trialDaysRemaining || 0)
  const trialEndDate = computed(() => user.value?.trialEndDate ? new Date(user.value.trialEndDate) : null)
  // Derived from tosAcceptedAt so this matches RequireVerifiedAccount on the
  // API exactly. The server refuses module settings, overlay tokens and
  // Timekeeper whenever tosAcceptedAt is absent, so that same field has to be
  // what raises the modal. Keying off accountPendingActivation instead misses
  // every account where the flag is absent rather than true, admitting the user
  // to a dashboard whose writes all 403.
  const needsTermsAcceptance = computed(() => !!user.value && !user.value.tosAcceptedAt)
  const needsEmailVerification = computed(() => user.value?.tosAcceptedAt && !user.value?.emailVerified)
  const hasAcceptedTerms = computed(() => !!user.value?.tosAcceptedAt)
  const emailVerificationSentAt = computed(() => user.value?.emailVerificationSentAt ? new Date(user.value.emailVerificationSentAt) : null)

  // Twitch connection. twitchDisconnected stays true until a login restores
  // the token, so the prompt persists across page loads rather than depending
  // on catching a single event.
  const twitchDisconnected = computed(() => user.value?.twitchDisconnected === true)
  const twitchDisconnectedAt = computed(() => user.value?.twitchDisconnectedAt ? new Date(user.value.twitchDisconnectedAt) : null)

  // Only set when the reconnect began from the emailed link. Someone who
  // simply logged in again has already fixed it and does not need telling.
  const showReconnectSuccess = computed(() => user.value?.showReconnectSuccess === true)

  // Actions
  async function checkAuth() {
    try {
      loading.value = true
      error.value = null

      // Fetch user profile (cookie-based authentication)
      const response = await apiClient.get('/api/auth/me')
      user.value = response.data

      initialized.value = true
      return true
    } catch (err) {
      console.error('Auth check failed:', err)
      // User is not authenticated
      user.value = null
      initialized.value = true
      return false
    } finally {
      loading.value = false
    }
  }

  async function login(credentials) {
    try {
      loading.value = true
      error.value = null

      const response = await apiClient.post('/api/auth/login', credentials)

      const { token: authToken, user: userData } = response.data

      // Store token
      token.value = authToken
      localStorage.setItem('auth_token', authToken)

      // Set token in API client
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`

      // Store user data
      user.value = userData

      initialized.value = true
      return { success: true }
    } catch (err) {
      console.error('Login failed:', err)
      error.value = err.response?.data?.message || 'Login failed. Please check your credentials.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function register(userData) {
    try {
      loading.value = true
      error.value = null

      const response = await apiClient.post('/api/auth/register', userData)

      const { token: authToken, user: newUser } = response.data

      // Store token
      token.value = authToken
      localStorage.setItem('auth_token', authToken)

      // Set token in API client
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`

      // Store user data
      user.value = newUser

      initialized.value = true
      return { success: true }
    } catch (err) {
      console.error('Registration failed:', err)
      error.value = err.response?.data?.message || 'Registration failed. Please try again.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      // Call logout endpoint (cookie-based)
      await apiClient.post('/api/auth/logout').catch(() => {})
    } finally {
      // Clear local state regardless of API call result
      token.value = null
      user.value = null
      localStorage.removeItem('auth_token')
      delete apiClient.defaults.headers.common['Authorization']
      initialized.value = true
    }
  }

  async function updateProfile(profileData) {
    try {
      loading.value = true
      error.value = null

      const response = await apiClient.put('/api/auth/profile', profileData)
      user.value = response.data

      return { success: true }
    } catch (err) {
      console.error('Profile update failed:', err)
      error.value = err.response?.data?.message || 'Profile update failed.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function changePassword(passwordData) {
    try {
      loading.value = true
      error.value = null

      await apiClient.post('/api/auth/change-password', passwordData)

      return { success: true }
    } catch (err) {
      console.error('Password change failed:', err)
      error.value = err.response?.data?.message || 'Password change failed.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function refreshToken() {
    try {
      const response = await apiClient.post('/api/auth/refresh')
      const { token: newToken } = response.data

      token.value = newToken
      localStorage.setItem('auth_token', newToken)
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

      return true
    } catch (err) {
      console.error('Token refresh failed:', err)
      await logout()
      return false
    }
  }

  // OAuth/External login
  async function loginWithProvider(provider, code) {
    try {
      loading.value = true
      error.value = null

      const response = await apiClient.post(`/api/auth/${provider}/callback`, { code })

      const { token: authToken, user: userData } = response.data

      token.value = authToken
      localStorage.setItem('auth_token', authToken)
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
      user.value = userData

      initialized.value = true
      return { success: true }
    } catch (err) {
      console.error(`${provider} login failed:`, err)
      error.value = err.response?.data?.message || `${provider} login failed.`
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // Accept terms of service and start free trial
  /// Dismisses the reconnect confirmation so it appears only once.
  async function acknowledgeReconnect() {
    // Cleared locally first so the dialog closes immediately, then on the
    // server so it does not return on the next load.
    if (user.value) user.value.showReconnectSuccess = false
    try {
      await apiClient.post('/api/auth/reconnect-acknowledge')
    } catch (e) {
      // Not worth surfacing: the dialog is already closed, and the worst case
      // is seeing it once more later.
    }
  }

  async function acceptTerms(email, marketingOptIn) {
    try {
      loading.value = true
      error.value = null

      const response = await apiClient.post('/api/auth/accept-terms', { email, marketingOptIn })

      // Refresh user data to get updated trial info
      await checkAuth()

      return { success: true, data: response.data }
    } catch (err) {
      console.error('Accept terms failed:', err)
      error.value = err.response?.data?.error || 'Failed to accept terms. Please try again.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // Cancel registration and delete account
  async function cancelRegistration() {
    try {
      loading.value = true
      error.value = null

      await apiClient.delete('/api/auth/cancel-registration')

      // Clear local state - user account is deleted
      token.value = null
      user.value = null
      localStorage.removeItem('auth_token')
      delete apiClient.defaults.headers.common['Authorization']
      initialized.value = true

      return { success: true }
    } catch (err) {
      console.error('Cancel registration failed:', err)
      error.value = err.response?.data?.error || 'Failed to cancel registration.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    token,
    initialized,
    loading,
    error,

    // Getters
    isAuthenticated,
    isAdmin,
    userName,
    userEmail,

    // Subscription/Trial getters
    subscriptionTier,
    isPro,
    enabledModules,
    hasProFor,
    isTrialActive,
    trialExpired,
    trialDaysRemaining,
    trialEndDate,
    needsTermsAcceptance,
    needsEmailVerification,
    hasAcceptedTerms,
    emailVerificationSentAt,
    twitchDisconnected,
    twitchDisconnectedAt,
    showReconnectSuccess,

    // Actions
    checkAuth,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshToken,
    loginWithProvider,
    acceptTerms,
    acknowledgeReconnect,
    cancelRegistration
  }
})
